import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconClose from '../IconClose/IconClose';
import VoiceSearch from './VoiceSearch/VoiceSearch';
import css from './AlgoliaKeywordSearch.module.css';
import IconCollection from '../IconCollection/IconCollection';
import classNames from 'classnames';
import SearchSuggestions, {
  getComboboxInputProps,
  handleSuggestionKeyDown,
} from '../SearchSuggestions/SearchSuggestions';
import { useSearchSuggestions } from '../../hooks/useSearchSuggestions';
import { useSuggestionOrigin } from '../../hooks/useSuggestionOrigin';

export default function KeywordInput(props) {
  const {
    onSearchChange,
    setIsSearchFocused,
    isSearchFocused,
    clearKeywordsClass,
    micClass,
    keyword,
    setKeyword,
    searchBoxRef,
    searchInputText,
    onFocus,
    isDesktopLayout,
    autoFocus,
    blockAutoScroll,
    showMap,
    isLandingPage,
    locale
  } = props;
  const [isInitial, setIsInitial] = useState(true);
  const inputRef = useRef(null);
  const isScrollingRef = useRef(false);
  const isUserInteracting = useRef(false);
  const lastFocusTimeRef = useRef(0);
  // Prevents onSearchChange from firing on the initial mount. Without this guard,
  // every mount — including remounts caused by sticky row transitions — would
  // schedule a search with the stale initial keyword value, triggering an
  // unnecessary history.push → loadData → queryAlgolia chain.
  const hasMountedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  // Set when a suggestion is chosen, so the keyword change it causes does not also schedule
  // the ordinary typing search.
  const skipNextDebounceRef = useRef(false);
  // The keyword the page has actually been searched for. hasMountedRef only guards the FIRST
  // run of the debounce effect, but the effect re-runs whenever `runSearch` changes identity —
  // and DesktopSearchBar passes an unmemoized onSearchChange, so that happens on every render.
  // The second run then scheduled a search for a keyword nobody had touched, which showed up
  // in a network capture as spurious `query: ""` searches firing alongside the real one.
  const lastSearchedKeywordRef = useRef(keyword);
  const keywordRef = useRef(keyword);
  const isVoiceListeningRef = useRef(false);
  useEffect(() => {
    keywordRef.current = keyword;
  }, [keyword]);

  // Debouncing the search input changes to prevent excessive re-renders
  useEffect(() => {
    const isMobile = window.innerWidth <= 768; // Adjust breakpoint as needed
    if (autoFocus && !isMobile) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Mobile keyboard close during scroll
  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      const now = Date.now();
      const timeSinceFocus = now - lastFocusTimeRef.current;
      // Grace period: ignore scroll events within 500ms of focus (iOS auto-scroll)
      const isWithinFocusGracePeriod = timeSinceFocus < 500;

      // Only blur if:
      // 1. Input is focused
      // 2. User is not actively interacting
      // 3. Scroll is NOT within the grace period after focus (not iOS auto-scroll)
      if (
        typeof document !== 'undefined' &&
        inputRef.current === document.activeElement &&
        !isUserInteracting.current &&
        !isWithinFocusGracePeriod
      ) {
        inputRef.current.blur(); // Explicitly blur to close the keyboard if the user isn't interacting
      }

      isScrollingRef.current = true;

      // Only reset the interaction flag if it's not a focus-related auto-scroll
      // This prevents the flag from being reset during iOS's automatic scroll on focus
      if (!isWithinFocusGracePeriod) {
        isUserInteracting.current = false;
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isScrollingRef.current = false;
      }, 200);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Fires the actual search + related scroll behaviour; shared by the normal
  // typing debounce and the "flush immediately" path used when voice input
  // finishes, so voice search doesn't wait out the full typing debounce.
  const runSearch = useCallback(value => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (value !== undefined && value !== null) {
      lastSearchedKeywordRef.current = value;
      onSearchChange(String(value).trim());
    }

    if (typeof window !== 'undefined' && window.scrollTo && !isDesktopLayout && !blockAutoScroll && showMap) {
      window.scrollTo({
        top: 250,
        behavior: 'smooth',
      });
    }

    if (searchBoxRef?.current) {
      searchBoxRef.current.scrollTo(0, 0);
    }
  }, [onSearchChange, isDesktopLayout, blockAutoScroll, showMap, searchBoxRef]);

  // Debounced search logic
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    // While voice is actively listening, interim results (roughly one per
    // spoken word) would otherwise reset this timer constantly and — worse —
    // race with the flush that fires the instant listening stops (see
    // handleVoiceListeningChange below), firing multiple out-of-order
    // searches for the same utterance. Let the box update live, but only
    // search once, when the utterance is actually finished.
    if (isVoiceListeningRef.current) {
      return;
    }
    // A selection has already run this exact search synchronously; scheduling it again would
    // duplicate the query and make the results flicker as the second one lands.
    if (skipNextDebounceRef.current) {
      skipNextDebounceRef.current = false;
      return;
    }
    // Only a genuine change in what the customer typed warrants a search. Re-running because a
    // prop changed identity does not.
    if (keyword === lastSearchedKeywordRef.current) {
      return;
    }
    debounceTimerRef.current = setTimeout(() => runSearch(keyword), 900);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [keyword, runSearch]);

  const handleChange = text => {
    if (!isSearchFocused) {
      setIsSearchFocused(true);
    }
    setKeyword(text);
  };

  // Voice search stopped (silence, manual stop, or an error) — search
  // immediately instead of waiting out the typing debounce, so results
  // appear right when the user finishes speaking. While listening is still
  // true, the debounce effect above is suppressed entirely (see there for why).
  const handleVoiceListeningChange = useCallback(isListening => {
    isVoiceListeningRef.current = isListening;
    if (!isListening && hasMountedRef.current) {
      runSearch(keywordRef.current);
    }
  }, [runSearch]);

  const handleFocus = () => {
    // On the landing page the input is just a gateway to the search page —
    // always allow the redirect regardless of scroll state.
    if (isScrollingRef.current && !isLandingPage) {
      return;
    }

    // Reset initial state if focus is triggered after the first time
    if (isInitial) {
      setIsInitial(false);
    } else {
      setIsSearchFocused(true);
    }

    // Mark that the user is interacting
    isUserInteracting.current = true;

    // Record the focus time to ignore auto-scroll events that iOS triggers
    lastFocusTimeRef.current = Date.now();

    // Trigger any provided `onFocus` callback
    if (typeof onFocus === 'function') {
      onFocus();
    }
  };

  const handleBlur = () => {
    // Reset the interaction flag when input loses focus
    isUserInteracting.current = false;
  };

  // Type-ahead suggestions (RAS-2536). Kept entirely separate from the 900ms search debounce
  // above: this list updates while you type, that one commits a full search.
  const suggestionOrigin = useSuggestionOrigin();
  const suggestionsListboxId = 'marketplace-search-suggestions';
  const suggestions = useSearchSuggestions({
    query: keyword,
    locale,
    aroundLatLng: suggestionOrigin,
    // The "See all results" row is an option too, so the arrow keys can reach it.
    extraOptions: 1,
  });

  // Selecting a suggestion is an explicit commit, so it flushes rather than waiting out the
  // typing debounce — runSearch clears the pending timer itself.
  const handleSuggestionSelect = useCallback(
    value => {
      // Writing the selection into the box would otherwise schedule a SECOND search 900ms
      // later, on top of the one flushed here — two identical queries per selection, with the
      // results visibly settling twice.
      skipNextDebounceRef.current = true;
      setKeyword(value);
      // Pass the selected value: the input still holds the half-typed text at this point, so
      // the dismissal has to name the text it covers or the list reopens on the next render.
      suggestions.close(value);
      runSearch(value);
      if (inputRef.current) inputRef.current.blur();
    },
    [setKeyword, suggestions, runSearch]
  );

  const handleKeyDown = (e) => {
    // Arrow keys, Enter-on-a-suggestion and Escape belong to the dropdown while it is open.
    if (handleSuggestionKeyDown(e, {
      isOpen: suggestions.isOpen,
      highlighted: suggestions.highlighted,
      moveHighlight: suggestions.moveHighlight,
      onSelect: handleSuggestionSelect,
      onClose: suggestions.close,
    })) {
      return;
    }

    // Close dropdown on Enter/Return key press
    if (e.key === 'Enter' || e.key === 'Return') {
      suggestions.close();
      // Trigger blur to close the dropdown
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const searchIconWidthHeight = isDesktopLayout ? '16px' : '16px';

  return (
    <div className={css.keywordSearchContainer}>
      <div className={css.keywordSearchWrapper}>
        <input
          className={css.keywordSearch}
          ref={inputRef}
          value={keyword}
          onChange={e => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          type="text"
          placeholder={searchInputText || 'Search by keyword'}
          onFocus={handleFocus}
          onBlur={handleBlur}  // Track when the input loses focus
          {...suggestions.compositionHandlers}
          {...getComboboxInputProps({
            isOpen: suggestions.isOpen,
            listboxId: suggestionsListboxId,
            highlightedIndex: suggestions.highlightedIndex,
          })}
        />
        {!!keyword ? (
          <button
            className={clearKeywordsClass}
            type="button"
            onClick={() => {
              setKeyword('');
              // onSearchChange('');
              if (searchBoxRef && searchBoxRef.current) {
                searchBoxRef.current.scrollTo(0, 0);
              }
            }}
          >
            <IconClose size="small" />
          </button>
        ) : !isLandingPage ? <IconCollection width={searchIconWidthHeight} height={searchIconWidthHeight} className={css.searchIcon} name="search" /> : null}
        <VoiceSearch
          className={micClass}
          onVoiceSearch={transcript => handleChange(transcript)}
          onListeningChange={handleVoiceListeningChange}
          onActivateOverride={isLandingPage ? handleFocus : undefined}
        />
      </div>

      <SearchSuggestions
        suggestions={suggestions.suggestions}
        query={keyword}
        isOpen={suggestions.isOpen && isSearchFocused}
        highlightedIndex={suggestions.highlightedIndex}
        onSelect={handleSuggestionSelect}
        onHighlight={suggestions.setHighlightedIndex}
        listboxId={suggestionsListboxId}
      />
    </div>
  );
}
